import { AuthService } from '../shared/service/auth.service';
import { SocketService } from '../shared/service/socket.service';
import { CommonModule } from "@angular/common";
import { Component, DestroyRef, HostListener, inject, OnInit, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { MsgService } from '../shared/service/msg.service';
import { Login } from '../shared/model/usuario/login.model';
import { ActivatedRoute, Router } from '@angular/router';
import { ConstantsUtils } from '../shared/util/constants.utils';
import * as CryptoJS from 'crypto-js';
import { MatButtonModule } from '@angular/material/button';
import {FormFieldComponent} from '../shared/components/form-field/form-field.component';
import {MethodsUtils} from '../shared/util/methods.utils';
import {GrupoSelect} from '../shared/model/grupo/grupo-select.model';
import {SelectOneComponent} from '../shared/components/select-one/select-one.component';
import {CodigoRedefinicaoComponent} from './codigo-redefinicao/codigo-redefinicao.component';
import {TrocaSenhaComponent} from './troca-senha/troca-senha.component';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../environments/environment';
import { Ambiente } from '../shared/enum/ambiente.enum';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    FormFieldComponent,
    SelectOneComponent,
    CodigoRedefinicaoComponent,
    TrocaSenhaComponent,
    FormsModule
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit{
  private static readonly ATRASO_BUSCA_GRUPOS_MS = 500;

  login: Login = new Login();
  grupos: GrupoSelect[] = [];
  exibicao = {
    email: true,
    grupo: true,
    senha: true,
    codigoRedefiniciao: false,
    trocaSenha: false
  }
  codigoRedefinicaoSenha = '';

  @ViewChild('senhaInput') senhaInput!: any;

  email = '';
  ehMobile = false;
  isProd: boolean = environment.ambiente == Ambiente.PROD;

  private readonly authService = inject(AuthService);
  private readonly msgService = inject(MsgService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly socketService = inject(SocketService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly emailDigitado$ = new Subject<string>();

  ngOnInit(): void {
    this.atualizarEhMobile()
    this.emailDigitado$.pipe(
      debounceTime(LoginComponent.ATRASO_BUSCA_GRUPOS_MS),
      distinctUntilChanged(),
      filter(email => MethodsUtils.validarEmail(email)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.buscarGrupos(false));
    const login = localStorage.getItem('loginUsario');
    if (login) {
      this.login.login = login;
      this.buscarGrupos(false);
    }
    this.verificarPermissoes();
  }

  private atualizarEhMobile() {
    this.ehMobile = MethodsUtils.resolucaoPolegadas() < 11;
  }

  @HostListener('window:resize', [])
  onResize() {
    this.atualizarEhMobile();
  }

  @HostListener('document:keydown.enter', ['$event'])
  handleEnterKey(): void {
    this.logar();
  }

  autenticar(){
    if (!this.authService.token()) {
      if (!this.login.login || !this.login.senha || !this.login.grupo) {
        this.msgService.msgErro('Preencha todos os campos!');
        return;
      }
      this.authService.login(this.login.login, this.login.senha, this.login.grupo?.id).subscribe({
        next: (jwt) => {
          if (this.login.login) { localStorage.setItem('loginUsario', this.login.login!); }
          this.redirecionar(jwt);
        },
        error: (error) => this.msgService.msgErro(error.error)
      });
    } else {
      this.redirecionar({ idToken: this.authService.token() })
    }
  }

  redefinirSenha(){
    if(this.login.login && this.login.grupo) {
      this.authService.redefinirSenha(this.login.login!, this.login.grupo?.id ?? 0).subscribe({
        next: (res) => {
          if (res) {
            this.msgService.msgSucesso('E-Mail enviado');

            this.exibicao.senha = false;
            this.exibicao.grupo = false;
            this.exibicao.email = false;
            this.exibicao.codigoRedefiniciao = true;
          } else {
            this.msgService.msgAdvertencia('Usuário AD, não é possível redefinir a senha.');
          }
        }
      });
    } else this.msgService.msgAdvertencia('Preencha o E-Mail ou o Grupo');
  }

  validarCodigoRedefiniciao(){
    if(this.exibicao.codigoRedefiniciao && this.codigoRedefinicaoSenha){
      this.authService.validarCodigo(this.login.login!, this.login.grupo?.id ?? 0, this.codigoRedefinicaoSenha).subscribe({
        next: (res) => {
          if (res) {
            this.exibicao.codigoRedefiniciao = false;
            this.exibicao.trocaSenha = true;
            this.msgService.msgSucesso('Código validado com sucesso!');
          }
        }
      });
    }
  }

  selecionarGrupo(grupo: GrupoSelect){
    this.login.grupo = grupo;
    if (this.login.grupo) {
      this.exibicao.grupo = true;
      this.exibicao.senha = true;
    }
  }

  alterarSenha(senha:string){
    this.authService.trocarSenhaUsuario(this.login.login!, this.login.grupo?.id ?? 0, senha).subscribe({
      next: (res) => {
        if(res){
          this.exibicao.trocaSenha = false;
          this.exibicao.email = true;

          this.msgService.msgSucesso('Senha redefinida com sucesso');
        }
      },
      error: () => this.msgService.msgErro('Ocorreu um erro ao tentar trocar a senha')
    });
  }

  buscarCodigoRedefiniciao(codigo: string){
    this.codigoRedefinicaoSenha = codigo;
    this.validarCodigoRedefiniciao();
  }

  verificarPermissoes(){
    this.route.queryParams.subscribe({
      next: res => {
        if(res['token'] && res['token'] !== localStorage.getItem('tokenClariBot')) {
          this.redirecionar({ idToken: res['token'] });
        } else if (localStorage.getItem('permissoesClariBot')) {
          this.router.navigateByUrl(this.authService.ultimaRotinaAcessada());
        }
      }
    });
  }

  redirecionar(jwt: any): void {
    this.authService.armazenarToken(jwt);
    this.authService.buscarModulos().subscribe({
      next : (res: any) => {
        localStorage.setItem('permissoesClariBot', CryptoJS.AES.encrypt(JSON.stringify(res), ConstantsUtils.SECRET_KEY).toString());
        this.socketService.connect();
        this.socketService.registerTransferenciaTopicIfNeeded();
        this.verificarPermissoes();
      },
      error : () => this.authService.logout()
    });
  }

  logar(): void {
    const senhaPreenchida = this.senhaInput?.internalInput?.nativeElement?.value;
    if (!this.login.senha && senhaPreenchida) this.login.senha = senhaPreenchida;
    if (this.grupos.length === 0) {
      this.buscarGrupos(true);
    } else if(this.exibicao.codigoRedefiniciao) {
      this.validarCodigoRedefiniciao();
    } else if(!this.login.senha || this.login.senha === '') {
      this.msgService.msgAdvertencia('Digite uma senha válida');
    } else if(this.login.login && this.login.grupo) {
      if (!this.validarLogin()) {return; }
      this.autenticar();
    } else {
      this.msgService.msgAdvertencia('Email ou grupo não preenchidos');
    }
  }

  validarLogin(){
    if (!this.login.login){
      this.msgService.msgAdvertencia('Digite um login');
      return false;
    } else if (!this.login.login.includes('@')) {
      this.msgService.msgAdvertencia('Digite um email válido.');
      return false;
    } else if (this.login.senha == null || !this.login.senha.length) {
      this.msgService.msgAdvertencia('Digite uma senha válida.');
      return false;
    } else {
      return true;
    }
  }

  buscarGrupos(avisar: boolean) {
    const email = (this.login.login ?? '').trim();
    if (MethodsUtils.validarEmail(email)) {
      this.authService.buscarGruposPeloEmailUsuario(email).subscribe({
        next: res => {
          if ((this.login.login ?? '').trim() !== email) return;
          this.grupos = res;
          if (this.grupos.length == 1) {
            this.login.grupo = this.grupos[0];

            this.exibicao.email = true;
            this.exibicao.grupo = true;
            this.exibicao.senha = true;
          } else if(this.grupos.length > 1) {
            this.exibicao.email = true;
            this.exibicao.grupo = true;
            this.exibicao.senha = true;
          } else if (avisar) {
            this.msgService.msgAdvertencia('E-Mail não encontrado.');
          }
        }
      });
    } else if (avisar) {
      this.msgService.msgAdvertencia('E-Mail inválido, verifique a escrita.');
    }
  }

  selecionarEmail(event: string) {
    const email = (event ?? '').trim();
    if (email !== (this.login.login ?? '').trim()) {
      this.grupos = [];
      this.login.grupo = undefined;
    }
    this.login.login = event;
    this.emailDigitado$.next(email);
  }

  voltar(valor: string){
    if(valor === 'redefinicaoSenha') {
      this.exibicao.codigoRedefiniciao = false;
      this.exibicao.senha = true;
      this.exibicao.email = true;
      this.exibicao.grupo = true;
    }
  }

  getApresentacao() {
    if (this.exibicao.codigoRedefiniciao || this.exibicao.trocaSenha) {
      return 'Redefinição de senha';
    } else {
      const hora = new Date().getHours();
      return (hora > 5 && hora < 12) ? 'Bom dia :)' : (hora >= 12 && hora <= 17) ? 'Boa tarde :)' : 'Boa noite :)';
    }
  }
}
